import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Bot, Send, ShieldCheck, RefreshCw, ArrowLeft } from 'lucide-react';
import { Spinner, LoadingState } from '@systemdox/ui';
import { guardrailsApi, type GuardrailEntry, type AiChatMessage } from '@/services/guardrailsApi';
import { cn } from '@/lib/utils';

export default function AiChatPage() {
  const [messages, setMessages] = useState<AiChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [guardrails, setGuardrails] = useState<GuardrailEntry[]>([]);
  const [loadingGuardrails, setLoadingGuardrails] = useState(true);
  const [showContext, setShowContext] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    guardrailsApi
      .listEntries()
      .then(setGuardrails)
      .finally(() => setLoadingGuardrails(false));
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  async function handleSend() {
    if (!input.trim() || sending) return;
    const userMsg: AiChatMessage = { role: 'user', content: input };
    const updated = [...messages, userMsg];
    setMessages(updated);
    setInput('');
    setSending(true);
    try {
      const { reply } = await guardrailsApi.chat(updated);
      setMessages([...updated, { role: 'assistant', content: reply }]);
    } catch {
      setMessages([
        ...updated,
        { role: 'assistant', content: 'Sorry, something went wrong. Please try again.' },
      ]);
    } finally {
      setSending(false);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  function handleClear() {
    setMessages([]);
    setInput('');
  }

  return (
    <div className="flex h-full">
      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col h-full">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-800">
          <div className="flex items-center gap-3">
            <Link
              to="/ai"
              className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 transition-colors mr-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Link>
            <div className="w-8 h-8 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
              <Bot className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-slate-900 dark:text-slate-100">Ask AI</h1>
              <p className="text-xs text-slate-500">
                Context-aware coding assistant with {guardrails.length} check
                {guardrails.length !== 1 ? 's' : ''} loaded
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowContext(!showContext)}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors',
                showContext
                  ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
                  : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800'
              )}
            >
              <ShieldCheck className="w-3.5 h-3.5" />
              {showContext ? 'Hide' : 'Show'} Context
            </button>
            <button
              onClick={handleClear}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Clear
            </button>
          </div>
        </div>

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-4">
          {messages.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <Bot className="w-16 h-16 text-slate-200 dark:text-slate-700 mb-4" />
              <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
                Context-Aware AI Assistant
              </h2>
              <p className="text-sm text-slate-500 max-w-md">
                Ask me to help build features, write code, or solve problems. I'm aware of your
                team's common AI mistakes and will avoid them. Your checks knowledge base is
                automatically injected as context.
              </p>
              <div className="mt-6 grid grid-cols-2 gap-3 max-w-lg">
                {[
                  'Write a Lambda handler that extracts user identity',
                  'Create a React component with error tracking',
                  'Add a new API endpoint with proper auth',
                  'Review this code for common AI mistakes',
                ].map((suggestion) => (
                  <button
                    key={suggestion}
                    onClick={() => setInput(suggestion)}
                    className="text-left text-xs px-3 py-2.5 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg, idx) => (
            <div
              key={idx}
              className={cn('flex', msg.role === 'user' ? 'justify-end' : 'justify-start')}
            >
              <div
                className={cn(
                  'max-w-2xl rounded-lg px-4 py-3 text-sm whitespace-pre-wrap',
                  msg.role === 'user'
                    ? 'bg-purple-600 text-white'
                    : 'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-slate-100'
                )}
              >
                {msg.content}
              </div>
            </div>
          ))}

          {sending && (
            <div className="flex justify-start">
              <div className="bg-slate-100 dark:bg-slate-800 rounded-lg px-4 py-3">
                <Spinner size="md" aria-label="Assistant is replying" />
              </div>
            </div>
          )}
        </div>

        {/* Input */}
        <div className="border-t border-slate-200 dark:border-slate-800 p-4">
          <div className="flex items-end gap-3">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask the AI assistant... (Enter to send, Shift+Enter for new line)"
              rows={1}
              className="flex-1 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-4 py-3 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
              style={{ minHeight: '44px', maxHeight: '120px' }}
              onInput={(e) => {
                const el = e.currentTarget;
                el.style.height = 'auto';
                el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
              }}
            />
            <button
              onClick={handleSend}
              disabled={sending || !input.trim()}
              className="flex items-center justify-center w-10 h-10 rounded-lg bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50 shrink-0"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Context Sidebar */}
      {showContext && (
        <div className="w-80 border-l border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 overflow-y-auto">
          <div className="p-4">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-3">
              Injected Checks
            </h3>
            {loadingGuardrails ? (
              <LoadingState size="md" />
            ) : guardrails.length === 0 ? (
              <p className="text-xs text-slate-500 text-center mt-8">
                No checks yet. Add them from the Checks page.
              </p>
            ) : (
              <div className="space-y-2">
                {guardrails.map((g) => (
                  <div
                    key={g.id}
                    className="rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-3"
                  >
                    <div className="flex items-center gap-1.5 mb-1">
                      <span
                        className={cn(
                          'w-1.5 h-1.5 rounded-full',
                          g.severity === 'critical'
                            ? 'bg-red-500'
                            : g.severity === 'high'
                              ? 'bg-orange-500'
                              : g.severity === 'medium'
                                ? 'bg-amber-500'
                                : 'bg-blue-500'
                        )}
                      />
                      <p className="text-xs font-medium text-slate-900 dark:text-slate-100 truncate">
                        {g.title}
                      </p>
                    </div>
                    <p className="text-[11px] text-slate-500 line-clamp-2">{g.description}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
